// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  pointerWithin,
  DndContext,
  DragOverlay,
  DropAnimation,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  Modifiers,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
  defaultDropAnimationSideEffects,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  SortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
// Components, Layouts, Pages
import { DroppableContainerKanban, SortTableItemKanban, ItemKanban } from '~/components/common';
// Others
import { ChangeData, ColumnTypeKanban, IBaseKanban } from '~/utils/interface/common';
// Styles, images, icons
import styles from './BaseKanban.module.scss';

type Props<T extends IBaseKanban> = {
  data: ColumnTypeKanban<T>[];
  renderItem: (item: T, columnData?: ColumnTypeKanban<T>) => React.ReactNode;
  onChange: (changeData: ChangeData<T>) => void;
  strategy?: SortingStrategy;
  modifiers?: Modifiers;
  adjustScale?: boolean;
  gapColumn?: number;
  minHeightColumn?: number;
  disabled?: boolean;
  HeaderColumn?: React.ComponentType<{
    children: React.ReactNode;
    label: string;
    date?: string;
    index: number;
    uuid?: UniqueIdentifier;
    id: UniqueIdentifier;
    data: ColumnTypeKanban<T>;
  }>;
};

const cx = classNames.bind(styles);

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

const BaseKanban = <T extends IBaseKanban>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    data,
    renderItem,
    strategy = verticalListSortingStrategy,
    modifiers,
    minHeightColumn,
    gapColumn,
    adjustScale = false,
    disabled = false,
    onChange,
    HeaderColumn = DroppableContainerKanban,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const recentlyMovedToNewContainer = useRef(false);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [columns, setColumns] = useState<ColumnTypeKanban<T>[]>(data ?? []);
  const [clonedItems, setClonedItems] = useState<ColumnTypeKanban<T>[] | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (data) {
      setColumns(
        data.map((column) => ({
          ...column,
          items: column.items.map((item) => ({
            ...item,
            uuid: `item-${item.id || Math.random().toString()}`,
          })),
          uuid: `column-${column.id || Math.random().toString()}`,
        }))
      );
    }
  }, [data]);

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [columns]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const { id } = active;

    setActiveId(id);
    setClonedItems(columns);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      setClonedItems(null);

      const currentActiveContainer = findContainerIndex(active.id);

      if (currentActiveContainer !== null) {
        const currentColumn = columns[currentActiveContainer];
        const activeItemIndex = currentColumn.items.findIndex((item: T) => item.uuid === active.id);

        if (activeItemIndex !== -1) {
          const currentItem = currentColumn.items[activeItemIndex];

          onChange &&
            onChange({
              columnId: currentColumn.id,
              newIndex: activeItemIndex,
              oldColumnId: currentColumn.id,
              oldIndex: activeItemIndex,
              item: currentItem,
              allColumns: columns,
            });
        }
      }

      return;
    }

    const currentActiveContainer = findContainerIndex(active.id);
    const currentOverContainer = findContainerIndex(over.id);
    if (currentOverContainer !== null) {
      const overColumn = columns[currentOverContainer];

      if (overColumn.id === 'unassigned') {
        handleDragCancel();
        return;
      }
    }

    let originalActiveContainer: number | null = null;
    if (clonedItems) {
      for (let i = 0; i < clonedItems.length; i++) {
        const hasItem = clonedItems[i].items.some((item: T) => item.uuid === active.id);
        if (hasItem) {
          originalActiveContainer = i;
          break;
        }
      }
    }

    if (currentActiveContainer === null || currentOverContainer === null || originalActiveContainer === null) {
      setActiveId(null);
      setClonedItems(null);
      return;
    }

    if (originalActiveContainer === currentOverContainer && !recentlyMovedToNewContainer.current) {
      const isOverAnItem = columns[currentOverContainer].items.some((item: T) => item.uuid === over.id);

      if (isOverAnItem && active.id !== over.id) {
        setColumns((prevColumns) => {
          const newColumns = [...prevColumns];
          const column = newColumns[currentActiveContainer];
          const activeIndex = column.items.findIndex((item: T) => item.uuid === active.id);
          const overIndex = column.items.findIndex((item: T) => item.uuid === over.id);

          if (activeIndex !== -1 && overIndex !== -1) {
            const reorderedItems = arrayMove(column.items, activeIndex, overIndex);
            newColumns[currentActiveContainer] = {
              ...column,
              items: reorderedItems,
            };

            const movedItem = reorderedItems[overIndex];

            const updatedItems = reorderedItems.map((it, idx) => ({
              item: it,
              oldColumnId: column.id,
              newColumnId: column.id,
              oldIndex: clonedItems?.find((c) => c.id === column.id)?.items.findIndex((x) => x.uuid === it.uuid) ?? idx,
              newIndex: idx,
            }));

            onChange &&
              onChange({
                columnId: column.id,
                newIndex: overIndex,
                oldColumnId: column.id,
                oldIndex: activeIndex,
                item: movedItem,
                allColumns: newColumns,
                updatedItems,
              });
          }

          return newColumns;
        });
      }
    } else if (originalActiveContainer !== currentOverContainer) {
      let oldColumnId: string | null = null;
      let oldIndex = -1;
      let movedItem: T | undefined = undefined;

      if (clonedItems && originalActiveContainer !== null) {
        const originalColumn = clonedItems[originalActiveContainer];
        oldColumnId = originalColumn.id;
        oldIndex = originalColumn.items.findIndex((item: T) => item.uuid === active.id);
        movedItem = originalColumn.items[oldIndex];
      }

      if (!movedItem) {
        setActiveId(null);
        setClonedItems(null);
        return;
      }

      let newIndex: number;
      const currentOverColumn = columns[currentOverContainer];

      if (over.id === currentOverColumn.uuid) {
        newIndex = currentOverColumn.items.length;
      } else {
        const overItemIndex = currentOverColumn.items.findIndex((item: T) => item.uuid === over.id);

        if (overItemIndex === -1) {
          newIndex = currentOverColumn.items.length;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top > over.rect.top + over.rect.height;

          newIndex = overItemIndex + (isBelowOverItem ? 1 : 0);
        }
      }

      setColumns(() => {
        const newColumns = [...clonedItems!];

        const oldColumn = newColumns[originalActiveContainer!];
        const filteredOldItems = oldColumn.items.filter((item: T) => item.uuid !== active.id);

        const newColumn = newColumns[currentOverContainer];
        const newItems = [...newColumn.items];
        newItems.splice(newIndex, 0, movedItem!);

        newColumns[originalActiveContainer!] = {
          ...oldColumn,
          items: filteredOldItems,
        };
        newColumns[currentOverContainer] = {
          ...newColumn,
          items: newItems,
        };

        const updatedOld = filteredOldItems.map((it, idx) => ({
          item: it,
          oldColumnId: oldColumn.id,
          newColumnId: oldColumn.id,
          oldIndex: clonedItems?.find((c) => c.id === oldColumn.id)?.items.findIndex((x) => x.uuid === it.uuid) ?? idx,
          newIndex: idx,
        }));

        const updatedNew = newItems.map((it, idx) => ({
          item: it,
          oldColumnId: clonedItems?.find((c) => c.id === newColumn.id)?.id ?? newColumn.id,
          newColumnId: newColumn.id,
          oldIndex: clonedItems?.find((c) => c.id === newColumn.id)?.items.findIndex((x) => x.uuid === it.uuid) ?? idx,
          newIndex: idx,
        }));

        const updatedItems = [...updatedOld, ...updatedNew];

        onChange &&
          onChange({
            columnId: newColumn.id,
            newIndex: newIndex,
            oldColumnId: oldColumn.id,
            oldIndex: oldIndex,
            item: movedItem!,
            allColumns: newColumns,
            updatedItems,
          });

        return newColumns;
      });
    }

    setActiveId(null);
    setClonedItems(null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (over?.id == null) {
      return;
    }

    const isActiveColumn = columns.some((col) => col.uuid === active.id);
    if (isActiveColumn) {
      return;
    }

    const overContainerIndex = findContainerIndex(over.id);
    const activeContainerIndex = findContainerIndex(active.id);
    if (overContainerIndex === null || activeContainerIndex === null) return;

    const overColumn = columns[overContainerIndex];

    if (overColumn.id === 'unassigned') {
      return;
    }

    if (overContainerIndex === null || activeContainerIndex === null) {
      return;
    }

    if (activeContainerIndex === overContainerIndex) {
      return;
    }

    setColumns((prevColumns) => {
      const newColumns = [...prevColumns];
      const activeColumn = newColumns[activeContainerIndex];
      const overColumn = newColumns[overContainerIndex];

      const activeItemIndex = findItemIndex(activeContainerIndex, active.id);
      if (activeItemIndex === -1) return prevColumns;

      const activeItem = activeColumn.items[activeItemIndex];

      let newIndex: number;

      if (over.id === overColumn.uuid) {
        newIndex = overColumn.items.length;
      } else {
        const overItemIndex = findItemIndex(overContainerIndex, over.id);

        if (overItemIndex === -1) {
          newIndex = overColumn.items.length;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top > over.rect.top + over.rect.height;

          const modifier = isBelowOverItem ? 1 : 0;
          newIndex = overItemIndex + modifier;
        }
      }
      recentlyMovedToNewContainer.current = true;

      newColumns[activeContainerIndex] = {
        ...activeColumn,
        items: activeColumn.items.filter((_, index) => index !== activeItemIndex),
      };

      const newOverItems = [...overColumn.items];
      newOverItems.splice(newIndex, 0, activeItem);

      newColumns[overContainerIndex] = {
        ...overColumn,
        items: newOverItems,
      };

      return newColumns;
    });
  };

  const handleDragCancel = () => {
    if (clonedItems) {
      setColumns(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  const findContainerIndex = (uuid: UniqueIdentifier | undefined): number | null => {
    if (!uuid) return null;

    const columnIndex = columns.findIndex((column) => column.uuid === uuid);
    if (columnIndex !== -1) {
      return columnIndex;
    }

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      const hasItem = column.items.some((item: T) => item.uuid === uuid);
      if (hasItem) {
        return i;
      }
    }

    return null;
  };

  const findItemIndex = (columnIndex: number, itemId: UniqueIdentifier): number => {
    if (columnIndex < 0 || columnIndex >= columns.length) return -1;
    return columns[columnIndex].items.findIndex((item: T) => item.uuid === itemId);
  };

  const findItemAndContainer = (uuid: UniqueIdentifier | undefined) => {
    if (!uuid) return { item: null, container: null };

    for (const column of columns) {
      if (column.uuid === uuid) {
        return { item: null, container: column };
      }

      const item = column.items.find((item: T) => item.uuid === uuid);
      if (item) {
        return { item, container: column };
      }
    }

    return { item: null, container: null };
  };

  const renderSortableItemDragOverlay = (uuid: UniqueIdentifier) => {
    const { item, container } = findItemAndContainer(uuid);

    if (!item) {
      return null;
    }

    return (
      <ItemKanban
        dragOverlay
        data={item}
        columnData={container as ColumnTypeKanban<IBaseKanban>}
        renderItem={renderItem as (data: IBaseKanban, columnData?: ColumnTypeKanban<IBaseKanban>) => React.ReactNode}
      />
    );
  };
  //#endregion Handle Function

  return (
    <Box id='baseKanbanComponent' className={cx('container')}>
      <DndContext
        sensors={disabled ? [] : sensors}
        collisionDetection={pointerWithin}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        modifiers={modifiers}
      >
        <Box className={cx('columns')}>
          <SortableContext items={columns.map((i) => i.uuid!)} disabled={disabled}>
            <Box
              component={'div'}
              className={cx('columnsContainer')}
              style={{
                gap: gapColumn,
                minHeight: minHeightColumn,
              }}
            >
              {columns.map((col, colIndex) => {
                return (
                  <HeaderColumn
                    key={`${col.uuid!}-${colIndex}`}
                    index={colIndex}
                    label={col.name}
                    date={col?.date}
                    data={col}
                    id={col.uuid!}
                  >
                    <SortableContext items={col.items.map((i) => i.uuid!)} strategy={strategy} disabled={disabled}>
                      {col.items.map((item, index) => (
                        <SortTableItemKanban
                          key={`${item.uuid!}-${index}`}
                          data={item}
                          id={item.uuid!}
                          columnData={col}
                          index={index}
                          renderItem={(data, columnData) => renderItem(data, columnData)}
                        />
                      ))}
                    </SortableContext>
                  </HeaderColumn>
                );
              })}
            </Box>
          </SortableContext>
          <DragOverlay adjustScale={adjustScale} dropAnimation={dropAnimation}>
            {activeId ? renderSortableItemDragOverlay(activeId) : null}
          </DragOverlay>
        </Box>
      </DndContext>
    </Box>
  );
};

export default BaseKanban;
